import React from "react";
import { ExternalLink, Info } from "lucide-react";
import { StateBadge } from "./GwsBits";

/** Record-by-record view: Cloudflare state · public DNS · Google verification. */
export function GwsMatrixTable({ matrix, busy }) {
  return (
    <div className="border border-zinc-800 rounded-sm overflow-hidden" data-testid="gws-dns-matrix">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-zinc-950/60 text-zinc-500 uppercase tracking-wider text-[10px]">
            <th className="text-left px-3 py-2">Record</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Cloudflare</th>
            <th className="text-left px-3 py-2">Public DNS</th>
            <th className="text-left px-3 py-2">Google</th>
          </tr>
        </thead>
        <tbody>
          {matrix.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-center text-zinc-500 mono">{busy ? "Loading matrix…" : "No matrix yet — click refresh"}</td></tr>}
          {matrix.map((row) => <MatrixRow key={row.kind} row={row} />)}
        </tbody>
      </table>
    </div>
  );
}

function MatrixRow({ row }) {
  const skipped = Boolean(row.skip);
  return (
    <tr className="border-t border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`gws-row-${row.kind}`}>
      <td className="px-3 py-2 align-top">
        <div className="text-zinc-200 font-medium">{row.label}</div>
        {row.notes && <div className="mt-1 flex items-start gap-1 text-[10px] text-amber-400/80"><Info size={10} className="mt-[1px] shrink-0" /><span>{row.notes}</span></div>}
      </td>
      <td className="px-3 py-2 mono text-zinc-400">{row.type}</td>
      <td className="px-3 py-2 mono text-zinc-400 break-all max-w-[240px]">{row.name}</td>
      <td className="px-3 py-2">
        {skipped ? <span className="text-[11px] text-zinc-500 mono">n/a</span> : <StateBadge ok={row.cf_ok} label={row.cf_ok ? "written" : (row.cf_record_id ? "mismatch" : "missing")} />}
        {row.expected && (
          <details className="mt-1">
            <summary className="text-[10px] text-zinc-500 cursor-pointer hover:text-zinc-300">expected</summary>
            <div className="mono text-[10px] text-zinc-500 break-all mt-1 max-w-[240px]">{row.expected}</div>
            {row.cf_content && row.cf_content !== row.expected && <div className="mono text-[10px] text-amber-400/80 break-all mt-1 max-w-[240px]">actual: {row.cf_content}</div>}
          </details>
        )}
      </td>
      <td className="px-3 py-2">
        {skipped || !row.expected ? <span className="text-[11px] text-zinc-500 mono">—</span> : <StateBadge ok={row.resolved_ok} label={row.resolved_ok ? "resolves" : "propagating"} />}
      </td>
      <td className="px-3 py-2">
        {row.kind === "verification"
          ? <StateBadge ok={row.google_verified} label={row.google_verified ? "verified" : "pending"} />
          : row.kind === "dkim"
            ? <a href={row.help_url} target="_blank" rel="noreferrer" className="text-[11px] mono text-zinc-400 hover:text-white inline-flex items-center gap-1">console <ExternalLink size={10} /></a>
            : <span className="text-[11px] text-zinc-600 mono">—</span>}
      </td>
    </tr>
  );
}
